import api, { ApiResponse } from '../../../../api';
import Button from '../../../../components/button/Button';
import { endpoints } from '../../../../endpoints.config';
import { PaymentStepProps } from './MakePayment';
import { ItemConnectionAssetAccountModel } from 'pages/IbaneraNetwork/items/ItemConnectionAssetAccount';
import { useEffect, useState } from 'react';
import { ItemConnectionModel } from 'pages/IbaneraNetwork/items/ItemConnection';
import { Spinner } from 'components/spinner/Spinner';
import { useTranslation } from 'react-i18next';
import {
    useGetFiatAccountDetails,
    useGetFiatAccounts,
} from '../../../../helpers/useGetFiatAccountDetails';
import { Dropdown } from '../../../../components/form/Dropdown';

export type ErrorCountry = { name: string; countryISO3: string; errorCode: string | null };
type Props = PaymentStepProps & {
    connectionPayee: ItemConnectionModel;
    selectedSourceAccountId?: number;
    selectedAssetAccountId?: number;
};

export const RecipientAssetAccount: React.FC<Props> = ({
    nextStep,
    connectionPayee,
    selectedSourceAccountId,
    selectedAssetAccountId,
}) => {
    const { t } = useTranslation('common');
    const [loading, setLoading] = useState(false);
    const [selectedAssetAccountIdState, setSelectedAssetAccountId] = useState<number | undefined>(
        selectedAssetAccountId
    );
    const [assetAccountsList, setAssetAccountsList] = useState<ItemConnectionAssetAccountModel[]>(
        []
    );
    const routeAccount = useGetFiatAccountDetails();
    const usdAccounts = useGetFiatAccounts('USD');
    const sourceAccounts = routeAccount?.id != null ? [routeAccount] : usdAccounts;
    const [selectedSourceAccount, setSelectedSourceAccount] = useState<number | undefined>(
        selectedSourceAccountId
    );
    const selectedAssetAccount = assetAccountsList.find(
        (account) => account.id === selectedAssetAccountIdState
    );
    const sourceAccountOptions = sourceAccounts.map((account) => ({
        value: String(account.id),
        label: String(account.accountName ?? ''),
    }));
    const assetAccountOptions = assetAccountsList.map((account) => ({
        value: String(account.id),
        label: account.accountName,
    }));

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const response = await api.get<ApiResponse<ItemConnectionAssetAccountModel[]>>(
                    `${endpoints.networkmodule.getNetworkConnectionAssetsAccounts}`,
                    {
                        params: {
                            connectionCustomerID:
                                connectionPayee.customerId == connectionPayee.senderId
                                    ? connectionPayee.receiverId
                                    : connectionPayee.senderId,
                        },
                    }
                );

                const details = response.data?.details;
                setAssetAccountsList(Array.isArray(details) ? details : []);
            } catch (error: any) {
                setAssetAccountsList([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const handleAssetAccountChange = (option: any) => {
        setSelectedAssetAccountId(option ? Number(option.value) : undefined);
    };

    const handleSourceAccountChange = (option: any) => {
        setSelectedSourceAccount(option ? Number(option.value) : undefined);
    };

    const handleNextClick = () => {
        nextStep({
            payeeConnectionAssetAccount: selectedAssetAccount,
            sourceCustomerAssetAccountsId: selectedSourceAccount,
        });
    };

    useEffect(() => {
        setSelectedAssetAccountId((current) => {
            if (current != null && assetAccountsList.some((account) => account.id === current)) {
                return current;
            }
            if (
                selectedAssetAccountId != null &&
                assetAccountsList.some((account) => account.id === selectedAssetAccountId)
            ) {
                return selectedAssetAccountId;
            }
            return undefined;
        });
    }, [assetAccountsList, selectedAssetAccountId]);

    useEffect(() => {
        setSelectedSourceAccount((current) => {
            if (current != null && sourceAccounts.some((account) => account.id === current)) {
                return current;
            }
            if (
                selectedSourceAccountId != null &&
                sourceAccounts.some((account) => account.id === selectedSourceAccountId)
            ) {
                return selectedSourceAccountId;
            }
            return undefined;
        });
    }, [selectedSourceAccountId, sourceAccounts]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <h1 className="make-connect-payment-label">Connection:</h1>
            <span style={{ fontSize: '14px' }}>
                {connectionPayee.customerId === connectionPayee.senderId
                    ? connectionPayee.receiverName
                    : connectionPayee.senderName}
            </span>
            <h1 className="make-connect-payment-label">{t('sourceAccount')}</h1>
            <Dropdown
                id="source-account-dropdown"
                options={sourceAccountOptions}
                value={
                    sourceAccountOptions.find(
                        (option) => option.value === String(selectedSourceAccount)
                    ) ?? null
                }
                onChange={handleSourceAccountChange}
                roundedRectangle
                readOnly={sourceAccounts.length === 0}
                dropdownProps={{
                    placeholder: sourceAccounts.length
                        ? t('selectAccount')
                        : t('noSourceAccountsAvailable'),
                    isSearchable: false,
                    menuPlacement: 'auto',
                }}
            />
            <h1 className="make-connect-payment-label">
                {t('recipientAccount')} {loading && <Spinner className={'Relative'} />}
            </h1>

            <Dropdown
                id="item-dropdown"
                options={assetAccountOptions}
                value={
                    assetAccountOptions.find(
                        (option) => option.value === String(selectedAssetAccountIdState)
                    ) ?? null
                }
                onChange={handleAssetAccountChange}
                roundedRectangle
                readOnly={loading || assetAccountsList.length === 0}
                dropdownProps={{
                    placeholder: loading
                        ? t('loading')
                        : assetAccountsList.length
                          ? t('selectAccount')
                          : t('noRecipientAccountsAvailable'),
                    isSearchable: false,
                    menuPlacement: 'auto',
                }}
            />
            {!loading && assetAccountsList?.length > 0 && (
                <div className="Buttons" style={{ marginTop: '20px' }}>
                <Button
                    type="submit"
                    color="third"
                    onClick={handleNextClick}
                    disabled={loading || !selectedAssetAccount || selectedSourceAccount == null}
                >
                    {t('next')}
                </Button>
                </div>
            )}
        </div>
    );
};
